import { FC, useState, FormEvent, ChangeEvent } from 'react';
import { Container, Section, Card, CardTitle, CardDescription, Button, Input, Alert, Badge } from '../components/ui';
import { SEOHead } from '../components/seo';
import { ROUTES } from '../config/routes';
import { validateEmail, validateName, validatePhone } from '../utils/validation';
import { sanitizeString } from '../utils/sanitize';
import { trackLeadMagnetSubmission } from '../analytics/events';
import { apiClient } from '../api/client';
import { PageProps } from './HomePage';

export const FreeInterviewGuidePage: FC<PageProps> = ({ onNavigate }) => {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [currentStatus, setCurrentStatus] = useState('Job Seeker');
  const [consent, setConsent] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setGlobalError(null);
    const newErrors: Record<string, string> = {};

    const nameValidation = validateName(firstName);
    if (!nameValidation.isValid) {
      newErrors.firstName = nameValidation.error || 'Please enter your first name.';
    }

    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      newErrors.email = emailValidation.error || 'Please provide a valid email address.';
    }

    if (phone.trim()) {
      const phoneValidation = validatePhone(phone);
      if (!phoneValidation.isValid) {
        newErrors.phone = phoneValidation.error || 'Please enter a valid phone number.';
      }
    }

    if (!consent) {
      newErrors.consent = 'Please confirm your consent to receive the free guide.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    try {
      const response = await apiClient.post<
        {
          fullName: string;
          email: string;
          phone?: string;
          currentStatus: string;
          consentGiven: boolean;
          resourceSlug: string;
        },
        {
          success: boolean;
          data?: {
            leadId: string;
            resourceSlug: string;
            downloadToken: string;
          };
          error?: {
            code: string;
            message: string;
          };
        }
      >('/leads', {
        fullName: sanitizeString(firstName),
        email: sanitizeString(email),
        ...(phone.trim() ? { phone: sanitizeString(phone) } : {}),
        currentStatus: sanitizeString(currentStatus),
        consentGiven: true,
        resourceSlug: 'free-interview-guide',
      });

      if (!response.success || !response.data?.success) {
        const apiMessage =
          response.data?.error?.message ||
          response.error?.message ||
          'Unable to process your request at this time. Please try again.';

        throw new Error(apiMessage);
      }

      trackLeadMagnetSubmission(
        'free-interview-guide',
        sanitizeString(email),
      );

      if (!response.data.data?.downloadToken) {
        throw new Error('Download link could not be generated. Please try again.');
      }

      setIsSubmitting(false);
      onNavigate(ROUTES.THANK_YOU + '?token=' + encodeURIComponent(response.data.data.downloadToken));
    } catch (error: unknown) {
      setIsSubmitting(false);
      setGlobalError(
        error instanceof Error
          ? error.message
          : 'Unable to process your request at this time. Please try again.',
      );
    }
  };

  return (
    <>
      <SEOHead
        title="Free Interview Preparation Starter Guide � Connect Source"
        description="Download your complimentary 10-point pre-interview checklist and STAR framework guide. High-impact foundational interview readiness."
      />

      <Section style={{ backgroundColor: '#ffffff', borderBottom: '1px solid var(--color-border-subtle)', padding: '4.5rem 0' }}>
        <Container size="xl">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '3.5rem', alignItems: 'start' }}>
            
            {/* Left Column: Educational Content & Breakdown */}
            <div>
              <Badge variant="teal" style={{ marginBottom: '1rem' }}>Complimentary Career Resource</Badge>
              <h1 style={{ fontSize: 'clamp(2rem, 4vw, 2.75rem)', fontWeight: 800, color: 'var(--color-navy-dark)', lineHeight: 1.2 }}>
                Free Interview Preparation Starter Guide
              </h1>
              <p style={{ fontSize: '1.15rem', color: 'var(--color-text-secondary)', marginTop: '1rem', lineHeight: 1.65 }}>
                A practical, no-fluff foundational guide to help you structure answers with confidence, avoid rookie interview missteps, and approach hiring conversations prepared.
              </p>

              {/* What It Helps With */}
              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--color-navy-dark)', marginBottom: '0.75rem' }}>
                  What This Guide Helps With:
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                  <li style={{ display: 'flex', gap: '0.6rem', fontSize: '0.925rem', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                    <span style={{ color: 'var(--color-accent-teal)', fontWeight: 700 }}>&#10003;</span>
                    <span>Using a clear 10-point checklist to prepare for your interview.</span>
                  </li>
                  <li style={{ display: 'flex', gap: '0.6rem', fontSize: '0.925rem', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                    <span style={{ color: 'var(--color-accent-teal)', fontWeight: 700 }}>&#10003;</span>
                    <span>Using the foundational STAR framework to structure your interview responses.</span>
                  </li>
                  <li style={{ display: 'flex', gap: '0.6rem', fontSize: '0.925rem', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                    <span style={{ color: 'var(--color-accent-teal)', fontWeight: 700 }}>&#10003;</span>
                    <span>Avoiding the top 5 rookie interview mistakes before and during your interview.</span>
                  </li>
                </ul>
              </div>

              {/* Who Should Download */}
              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--color-navy-dark)', marginBottom: '0.75rem' }}>
                  Who Should Download This Guide:
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {['Students & New Grads', 'Active Job Seekers', 'Early Career Professionals', 'Career Switchers'].map((item, idx) => (
                    <Badge key={idx} variant="neutral">{item}</Badge>
                  ))}
                </div>
              </div>

              {/* What You Receive */}
              <div style={{ marginTop: '2rem', padding: '1.5rem', backgroundColor: 'var(--color-bg-canvas)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border-subtle)' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--color-navy-dark)', marginBottom: '0.5rem' }}>
                  What You Receive Inside (PDF Format):
                </h3>
                <ul style={{ paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.45rem', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
                  <li>The 10-Point Pre-Interview Readiness Checklist</li>
                  <li>The Foundational STAR Framework Template</li>
                  <li>Top 5 Most Common Rookie Interview Mistakes (and how to avoid them)</li>
                  <li>Day-of-Interview Confidence Preparation Protocol</li>
                </ul>
              </div>

              <div style={{ marginTop: '1.5rem', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                *Note: This complimentary starter guide is a standalone educational resource and is distinct from our comprehensive paid Interview Cracker Kit.
              </div>
            </div>

            {/* Right Column: Lead Capture Form */}
            <div>
              <Card style={{ padding: '2rem' }}>
                <Badge variant="primary" style={{ marginBottom: '0.75rem' }}>100% Free Instant Access</Badge>
                <CardTitle style={{ fontSize: '1.4rem' }}>Download Your Free Guide</CardTitle>
                <CardDescription style={{ marginTop: '0.35rem', marginBottom: '1.5rem', lineHeight: 1.5 }}>
                  Enter your details below to receive instant access. No credit card required.
                </CardDescription>

                {globalError && (
                  <Alert variant="error" title="Submission Error">
                    {globalError}
                  </Alert>
                )}

                <form onSubmit={handleSubmit} noValidate>
                  <div style={{ marginBottom: '1.15rem' }}>
                    <Input
                      id="lead-first-name"
                      label="First Name *"
                      type="text"
                      placeholder="e.g. Aditi"
                      value={firstName}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                      error={errors.firstName}
                      disabled={isSubmitting}
                      required
                    />
                  </div>

                  <div style={{ marginBottom: '1.15rem' }}>
                    <Input
                      id="lead-email"
                      label="Email Address *"
                      type="email"
                      placeholder="e.g. aditi@example.com"
                      value={email}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                      error={errors.email}
                      helperText="We will send the download link directly to this address."
                      disabled={isSubmitting}
                      required
                    />
                  </div>

                  <div style={{ marginBottom: '1.15rem' }}>
                    <Input
                      id="lead-phone"
                      label="WhatsApp / Phone Number"
                      type="tel"
                      placeholder="e.g. +91 98765 43210 (Optional)"
                      value={phone}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)}
                      error={errors.phone}
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Current Status Dropdown */}
                  <div style={{ marginBottom: '1.25rem' }}>
                    <label
                      htmlFor="current-status"
                      style={{
                        display: 'block',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: 'var(--color-navy-dark)',
                        marginBottom: '0.375rem'
                      }}
                    >
                      Current Status *
                    </label>
                    <select
                      id="current-status"
                      className="cs-select"
                      value={currentStatus}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => setCurrentStatus(e.target.value)}
                      disabled={isSubmitting}
                    >
                      <option value="Student">Student</option>
                      <option value="Fresher">Fresher (Recent Graduate)</option>
                      <option value="Working Professional">Working Professional</option>
                      <option value="Job Seeker">Active Job Seeker</option>
                      <option value="Career Switcher">Career Switcher</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Consent Checkbox */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.65rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={consent}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setConsent(e.target.checked)}
                        style={{ marginTop: '0.2rem' }}
                        disabled={isSubmitting}
                      />
                      <span style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', lineHeight: 1.45 }}>
                        I agree to receive the Free Interview Preparation Starter Guide and occasional career insights from Connect Source. Zero spam. Unsubscribe anytime.
                      </span>
                    </label>
                    {errors.consent && (
                      <div style={{ fontSize: '0.8rem', color: '#dc2626', marginTop: '0.35rem' }}>
                        {errors.consent}
                      </div>
                    )}
                  </div>

                  <Button
                    type="submit"
                    variant="teal"
                    size="lg"
                    style={{ width: '100%' }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Preparing Your Guide...' : 'Get Free Guide Now'}
                  </Button>
                </form>
              </Card>
            </div>

          </div>
        </Container>
      </Section>
    </>
  );
};













